/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react';
import {
  AccountIcon, BasketIcon, BrandLogo, ChevronDownIcon, HamburgerIcon, QuoteIcon, SearchIcon,
  SignoutIcon, LocationDetailIcon,
} from '@salesforce/retail-react-app/app/components/icons';
import Link from '@salesforce/retail-react-app/app/components/link';
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner';
import {
  Badge, Box, Button, Divider, Flex, IconButton, Popover,
  PopoverArrow, PopoverBody, PopoverContent, PopoverFooter, PopoverHeader, PopoverTrigger, Stack,
  Text, useDisclosure, useMediaQuery, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration';
import {STORE_LOCATOR_IS_ENABLED} from '@salesforce/retail-react-app/app/constants';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {messages, navLinks} from '@salesforce/retail-react-app/app/pages/account/constant';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {isHydrated, noop} from '@salesforce/retail-react-app/app/utils/utils';
import {
  ensureArray,
  getMyShowroomFromCookie,
  SHOWROOM_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';
import LocaleSelector from '../locale-selector';
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive';

const IconButtonWithRegistration = withRegistration(IconButton);

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
  children,
  onMenuClick = noop,
  onMyAccountClick = noop,
  onLogoClick = noop,
  onMyCartClick = noop,
  onWishlistClick = noop,
  onStoreLocatorClick = noop,
  onSearchBoxClick = noop,
  ...props
}) => {
  const intl = useIntl();
  const popoverTriggerRef = useRef(null);
  const {
    derivedData: {totalItems},
    data: basket,
  } = useCurrentBasket();
  const {isRegistered} = useCustomerType();
  const logout = useAuthHelper(AuthHelpers.Logout);
  const navigate = useNavigation();
  const {
    getButtonProps: getAccountMenuButtonProps,
    getDisclosureProps: getAccountMenuDisclosureProps,
    isOpen: isAccountMenuOpen,
    onClose: onAccountMenuClose,
    onOpen: onAccountMenuOpen,
  } = useDisclosure();
  const [isDesktop] = useMediaQuery('(min-width: 992px)');
  const {site, buildUrl} = useMultiSite();

  // State for user's preferred showroom
  const [myShowroom, setMyShowroom] = useState(null);

  // Load showroom from cookie on mount and listen for changes
  useEffect(() => {
    if (typeof window === 'undefined' || !site?.id) return;

    // Load initial value from cookie
    const savedShowroom = getMyShowroomFromCookie(site.id);

    if (savedShowroom) {
      setMyShowroom(savedShowroom);
    }

    // Listen for showroom changes from other components
    const handleShowroomChanged = (event) => {
      setMyShowroom(event.detail?.showroom || null);
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [site?.id]);

  const [showLoading, setShowLoading] = useState(false);
  // tracking if users enter the popover Content,
  // so we can decide whether to close the menu when users leave account icons
  const hasEnterPopoverContent = useRef();

  const styles = useMultiStyleConfig('Header');

  const onSignoutClick = async () => {
    setShowLoading(true);
    await logout.mutateAsync();
    navigate('/login');
    setShowLoading(false);
  };

  const handleIconsMouseLeave = () => {
    // don't close the menu if users enter the popover content
    setTimeout(() => {
      if (!hasEnterPopoverContent.current) onAccountMenuClose();
    }, 100);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' && event.shiftKey && isAccountMenuOpen) {
      // Prevent default behavior to keep focus on the popup trigger
      event.preventDefault();
      popoverTriggerRef.current.focus();
    }
  };

  return (
    <Box {...styles.container} {...props}>
      <Box {...styles.content}>
        {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}
        <Flex justifyContent="space-between" wrap="wrap">
          <IconButton
            icon={<BrandLogo {...styles.logo} />}
            aria-label={intl.formatMessage({
              id: 'header.button.assistive_msg.logo',
              defaultMessage: 'Logo',
            })}
            {...styles.iconLogo}
            variant="unstyled"
            onClick={onLogoClick}
          />
          <Box {...styles.actionsContainer}>
            {/* Desktop: Show store icon + text */}
            {STORE_LOCATOR_IS_ENABLED && (
              <Box display={{base: 'none', lg: 'block'}}>
                <Button
                  height="auto"
                  minWidth="auto"
                  paddingX={2}
                  variant="unstyled"
                  aria-label={myShowroom ?
                    intl.formatMessage({
                      defaultMessage: 'My Showroom: {name}',
                      id: 'header.button.assistive_msg.my_showroom',
                    }, {name: myShowroom.name}) :
                    intl.formatMessage({
                      defaultMessage: 'Set your showroom',
                      id: 'header.button.assistive_msg.set_showroom',
                    })}
                  onClick={() => {
                    if (myShowroom?.slug) {
                      navigate(buildUrl(`/showroom/${myShowroom.slug}`));
                    } else {
                      navigate(buildUrl('/showrooms'));
                    }
                  }}
                >
                  <Flex align="center" gap={2}>
                    <LocationDetailIcon boxSize={5} />
                    <Text
                      as="span"
                      color="primary.darkLinen"
                      fontSize="14px"
                      fontWeight="normal"
                      lineHeight="120%"
                      textDecoration="underline"
                      textDecorationThickness="1.4px"
                      textUnderlineOffset="2.8px"
                      whiteSpace="nowrap"
                    >
                      {myShowroom?.name || intl.formatMessage({
                        defaultMessage: 'Set your showroom',
                        id: 'header.button.set_showroom',
                      })}
                    </Text>
                  </Flex>
                </Button>
              </Box>
            )}

            {/* Mobile: Show store icon only */}
            {STORE_LOCATOR_IS_ENABLED && (
              <IconButton
                display={{base: 'block', lg: 'none'}}
                icon={<LocationDetailIcon />}
                aria-label={myShowroom ?
                  intl.formatMessage({
                    defaultMessage: 'My Showroom: {name}',
                    id: 'header.button.assistive_msg.my_showroom',
                  }, {name: myShowroom.name}) :
                  intl.formatMessage({
                    defaultMessage: 'Set your showroom',
                    id: 'header.button.assistive_msg.set_showroom',
                  })}
                {...styles.icons}
                variant="unstyled"
                onClick={() => {
                  if (myShowroom?.slug) {
                    navigate(buildUrl(`/showroom/${myShowroom.slug}`));
                  } else {
                    navigate(buildUrl('/showrooms'));
                  }
                }}
              />
            )}
            <IconButton
              icon={<SearchIcon />}
              {...styles.icons}
              variant="unstyled"
              onClick={onSearchBoxClick}
            />
            <Box {...styles.languageIconDesktop}>
              <LocaleSelector variant="desktop" />
            </Box>
            <Box className="account-menu-container" display={{base: 'none', lg: 'inline-flex'}}>
              <IconButtonWithRegistration
                icon={<AccountIcon />}
                variant="unstyled"
                aria-label={intl.formatMessage({
                  id: 'header.button.assistive_msg.my_account',
                  defaultMessage: 'My Account',
                })}
                {...styles.icons}
                {...styles.accountIcon}
                onClick={onMyAccountClick}
                onMouseOver={isDesktop ? onAccountMenuOpen : noop}
              />
              {isRegistered && isHydrated() && (
                <Popover
                  arrowSize={15}
                  isOpen={isAccountMenuOpen}
                  placement="bottom-end"
                  isLazy
                  onClose={onAccountMenuClose}
                  onOpen={onAccountMenuOpen}
                >
                  <PopoverTrigger>
                    <IconButton
                      icon={<ChevronDownIcon />}
                      variant="unstyled"
                      aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account_menu',
                        defaultMessage: 'Open account menu',
                      })}
                      {...styles.icons}
                      {...styles.arrowDown}
                      {...getAccountMenuButtonProps()}
                      ref={popoverTriggerRef}
                      onKeyDown={handleKeyDown}
                      onMouseLeave={handleIconsMouseLeave}
                      onMouseOver={onAccountMenuOpen}
                    />
                  </PopoverTrigger>

                  <PopoverContent
                    {...styles.popoverContent}
                    onMouseLeave={() => {
                      hasEnterPopoverContent.current = false;
                      onAccountMenuClose();
                    }}
                    onMouseOver={() => {
                      hasEnterPopoverContent.current = true;
                    }}
                    {...getAccountMenuDisclosureProps()}
                  >
                    <PopoverArrow />
                    <PopoverHeader>
                      <Text as="h2" fontFamily="body" fontSize="l" fontWeight="700">
                        {intl.formatMessage({
                          defaultMessage: 'My Account',
                          id: 'header.popover.title.my_account',
                        })}
                      </Text>
                    </PopoverHeader>
                    <PopoverBody>
                      <Box as="nav">
                        <Stack as="ul" data-testid="account-detail-nav" spacing={0}>
                          {ensureArray(navLinks).map((link) => {
                            const LinkIcon = link.icon;

                            return (
                              <Box
                                key={link.name}
                                as="li"
                                listStyleType="none"
                                position="relative"
                              >
                                <Button
                                  as={Link}
                                  leftIcon={<LinkIcon boxSize={5} />}
                                  to={`/account${link.path}`}
                                  useNavLink={true}
                                  variant="menu-link"
                                  width="100%"
                                >
                                  {intl.formatMessage(
                                    messages[link.name]
                                  )}
                                </Button>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </PopoverBody>
                    <PopoverFooter cursor="pointer" onClick={onSignoutClick}>
                      <Divider colorScheme="gray" />
                      <Button variant="unstyled" {...styles.signout}>
                        <Flex>
                          <SignoutIcon
                            aria-hidden={true}
                            boxSize={5}
                            {...styles.signoutIcon}
                          />
                          <Text as="span" {...styles.signoutText}>
                            {intl.formatMessage({
                              defaultMessage: 'Log out',
                              id: 'header.popover.action.log_out',
                            })}
                          </Text>
                        </Flex>
                      </Button>
                    </PopoverFooter>
                  </PopoverContent>
                </Popover>
              )}
            </Box>

            <IconButtonWithRegistration
              icon={<QuoteIcon />}
              variant="unstyled"
              aria-label={intl.formatMessage({
                defaultMessage: 'Wishlist',
                id: 'header.button.assistive_msg.wishlist',
              })}
              {...styles.icons}
              {...styles.wishlistIconDesktop}
              onClick={onWishlistClick}
            />
            <IconButton
              variant="unstyled"
              aria-label={intl.formatMessage(
                {
                  id: 'header.button.assistive_msg.my_cart_with_num_items',
                  defaultMessage: 'My cart, number of items: {numItems}',
                },
                {numItems: totalItems}
              )}
              icon={
                <>
                  <BasketIcon />
                  {basket && totalItems > 0 && (
                    <Badge variant="notification">{totalItems}</Badge>
                  )}
                </>
              }
              {...styles.icons}
              onClick={onMyCartClick}
            />

            <IconButton
              display={{lg: 'none'}}
              icon={<HamburgerIcon />}
              variant="unstyled"
              aria-label={intl.formatMessage({
                id: 'header.button.assistive_msg.menu',
                defaultMessage: 'Menu',
              })}
              title={intl.formatMessage({
                id: 'header.button.assistive_msg.menu.open_dialog',
                defaultMessage: 'Opens a dialog',
              })}
              {...styles.icons}
              onClick={onMenuClick}
            />
          </Box>

        </Flex>

        <Box {...styles.bodyContainer}>{children}</Box>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  children: PropTypes.node,
  onMenuClick: PropTypes.func,
  onLogoClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onWishlistClick: PropTypes.func,
  onMyCartClick: PropTypes.func,
  onStoreLocatorClick: PropTypes.func,
  onSearchBoxClick: PropTypes.func,
  searchInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.elementType})
  ]),
};

export default Header;
